/**
 * La marca de Aziru: el glifo y la palabra.
 *
 * POR QUE VIVE AQUI Y NO EN `features/demo/`
 * ------------------------------------------
 * Es el tercer inquilino que hace este mismo viaje, y siempre por la misma
 * razon: primero el glifo de WhatsApp (`components/WhatsAppIcon.tsx`), despues
 * el DATO de contacto (`lib/aziru.ts`), y ahora el dibujo. En cuanto una
 * pantalla del NEGOCIO lo pinta, dejarlo en el modulo de la demostracion ata el
 * mostrador a algo que un dia se puede borrar.
 *
 * Quien lo pinta ahora es el MARCO, o sea las ocho pantallas: la firma de Aziru
 * vive en la barra lateral y en el encabezado del telefono
 * (`components/AziruSignature.tsx`).
 *
 * LA GEOMETRIA ES EL LOGOTIPO, NO UNA APROXIMACION
 * La marca son dos figuras: un punto abajo a la izquierda y una barra inclinada
 * —un paralelogramo, no una diagonal con punta— que baja hacia la derecha. Las
 * medidas estan tomadas del archivo original y llevadas a un lienzo de
 * 37.5 x 27, que es su proporcion real. El lienzo NO es cuadrado a proposito:
 * encerrar una marca ancha en un cuadrado la deja flotando con aire arriba y
 * abajo, y al ponerla junto a la palabra se ve chica y descolgada.
 *
 * Por eso en el uso se le da alto y se le deja calcular el ancho (`h-10 w-auto`)
 * en lugar de `size-10`, que forzaria un cuadrado.
 *
 * LA ANIMACION SIGUE EL TRAZO
 * La barra se descubre de arriba hacia abajo —en el sentido en que uno la
 * dibujaria—, el punto aparece despues creciendo desde su centro, y la palabra
 * entra letra por letra. Poco mas de un segundo, una sola vez, cuando la firma
 * llega a pantalla. Con `prefers-reduced-motion` aparece ya puesta.
 *
 * El trazo va en `currentColor` para heredar la tinta del tema.
 */

import { useId, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { DURATION, EASE } from '../lib/motion.js';
import { AZIRU } from '../lib/aziru.js';
import { cx } from './ui.js';

/** Proporcion real de la marca: ancha, no cuadrada. */
export const VIEW_BOX = '0 0 37.5 27';
export const VIEW_W = 37.5;
export const VIEW_H = 27;

/** La barra: paralelogramo de lados verticales que baja hacia la derecha. */
export const BAR_PATH = 'M7.9 0H19.7L37 26.4H25.2Z';

/** El punto, apoyado abajo a la izquierda y ligeramente mas bajo que la barra. */
export const DOT = { cx: 6.4, cy: 20.6, r: 6.4 } as const;

/**
 * Tipografia de la palabra.
 *
 * El logotipo original es una grotesca muy pesada y muy cerrada; el tracking
 * negativo es lo que mas se le parece con la familia del sistema. Va aparte
 * para que la palabra se escriba igual en todos los sitios donde aparece.
 */
export const WORDMARK_CLS = 'font-extrabold tracking-[-0.045em] leading-none';

// ---------------------------------------------------------------------------
// Marca
// ---------------------------------------------------------------------------

/** La marca sola, quieta. Para firmas discretas (encabezado, pies, botones). */
export function AziruMark({ className }: { className?: string }): ReactNode {
  return (
    <svg
      viewBox={VIEW_BOX}
      className={cx('block', className)}
      fill="currentColor"
      role="img"
      aria-label={AZIRU.name}
    >
      <circle cx={DOT.cx} cy={DOT.cy} r={DOT.r} />
      <path d={BAR_PATH} />
    </svg>
  );
}

/**
 * Marca + palabra, con la entrada animada.
 *
 * `play` la dispara: quien la usa la pone en true cuando la firma llega a
 * pantalla, no al montarse. Montar doce capitulos de golpe y dejar que todos se
 * animen a la vez desperdicia la unica vez que esta animacion se ve.
 *
 * Los identificadores del recorte se derivan de `useId` porque los `id` de un
 * SVG son globales al documento: con dos firmas en pantalla —la bienvenida y la
 * placa del cierre— la segunda pisaria el recorte de la primera y una de las
 * dos se quedaria con la barra a medio descubrir.
 */
export function AziruLockup({
  play = true,
  className,
  markClassName = 'h-8 w-auto',
  wordClassName = 'text-4xl',
}: {
  play?: boolean;
  className?: string;
  markClassName?: string;
  wordClassName?: string;
}): ReactNode {
  const reduced = useReducedMotion();
  const uid = `az-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const state = play || reduced ? 'visible' : 'hidden';

  return (
    <div className={cx('inline-flex items-center gap-3 text-ink', className)}>
      <svg viewBox={VIEW_BOX} className={cx('block shrink-0', markClassName)} fill="currentColor" aria-hidden>
        <defs>
          <clipPath id={`${uid}-wipe`}>
            {/*
              El recorte crece de arriba abajo y descubre la barra en el sentido
              del trazo. Se anima la altura del recorte y no la opacidad de la
              barra: aparecer desvaneciendose es lo que hace cualquier cosa,
              descubrirse en el sentido del trazo es lo que hace un logotipo.
            */}
            <motion.rect
              x="0"
              y="0"
              width={VIEW_W}
              initial={reduced ? false : { height: 0 }}
              animate={{ height: state === 'visible' ? VIEW_H : 0 }}
              transition={{ duration: DURATION.slow, ease: EASE.out }}
            />
          </clipPath>
        </defs>

        <g clipPath={`url(#${uid}-wipe)`}>
          <path d={BAR_PATH} />
        </g>

        <motion.circle
          cx={DOT.cx}
          cy={DOT.cy}
          initial={reduced ? false : { r: 0 }}
          animate={{ r: state === 'visible' ? DOT.r : 0 }}
          transition={{ duration: DURATION.normal, ease: EASE.out, delay: reduced ? 0 : 0.3 }}
        />
      </svg>

      {/* La palabra entra letra por letra; el lector de pantalla lee una sola. */}
      <motion.span
        aria-label={AZIRU.name}
        className={cx('inline-flex', WORDMARK_CLS, wordClassName)}
        variants={reduced ? undefined : WORD_PARENT}
        initial={reduced ? false : 'hidden'}
        animate={state}
      >
        {AZIRU.name.split('').map((letter, i) => (
          <motion.span
            // Las letras de "Aziru" no se repiten, pero el indice deja el orden
            // explicito y aguanta si algun dia la palabra cambia.
            key={`${letter}-${i}`}
            aria-hidden
            variants={reduced ? undefined : LETTER}
            transition={{ duration: DURATION.normal, ease: EASE.out }}
          >
            {letter}
          </motion.span>
        ))}
      </motion.span>
    </div>
  );
}

const WORD_PARENT = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.045, delayChildren: 0.42 } },
} as const;

const LETTER = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0 },
} as const;
